import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";

export default function Terms() {
  const { lang, t } = useLanguage();

  useCmsPageSEO({
    title: lang === "ar" ? "شروط الخدمة | وافاي" : "Terms of Service | Wavai",
    description: lang === "ar" ? "شروط خدمة شركة وافاي تكنولوجي." : "Terms of service for Wavai Technology LLC.",
    robots: "noindex, follow",
    canonical: "https://www.wavai.ae/terms",
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container px-6 py-12 lg:py-32 max-w-3xl mx-auto">
        <h1 className="text-4xl font-display font-bold mb-2">
          {t("Terms of Service", "شروط الخدمة")}
        </h1>
        <p className="text-sm text-muted-foreground mb-12">
          {t("Last updated: March 4, 2026", "آخر تحديث: 4 مارس 2026")}
        </p>

        <section className="space-y-10 text-base leading-relaxed text-muted-foreground">
          {/* 1. Introduction */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("1. Introduction & Acceptance", "1. المقدمة والقبول")}
            </h2>
            <p>
              {t(
                "Welcome to the website of Wavai (\"Wavai,\" \"we,\" \"us,\" or \"our\"). These Terms of Service (\"Terms\") govern your access to and use of our website (wavai.ae) and any services, products, or content we provide. By accessing or using our website and services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our website or services.",
                "مرحباً بكم في موقع وافاي (\"وافاي\" أو \"نحن\" أو \"لنا\"). تحكم شروط الخدمة هذه (\"الشروط\") وصولك إلى واستخدامك لموقعنا الإلكتروني (wavai.ae) وأي خدمات أو منتجات أو محتوى نقدمه. باستخدامك لموقعنا وخدماتنا، فإنك توافق على الالتزام بهذه الشروط. إذا كنت لا توافق على هذه الشروط، يرجى عدم استخدام موقعنا أو خدماتنا."
              )}
            </p>
          </div>

          {/* 2. Description of Services */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("2. Description of Services", "2. وصف الخدمات")}
            </h2>
            <p>
              {t(
                "Wavai Technology LLC is a technology and digital services company specializing in e-commerce solutions, web design and development, AI integration, search engine optimization (SEO), digital marketing, email and SMS marketing, conversion rate optimization, and related consulting services. Our services are tailored for businesses operating in the GCC region and globally. Specific deliverables, timelines, and fees are defined in individual service agreements or proposals provided to each client.",
                "شركة ويفاي تكنولوجي ذ.م.م هي شركة تكنولوجيا وخدمات رقمية متخصصة في حلول التجارة الإلكترونية وتصميم وتطوير الويب وتكامل الذكاء الاصطناعي وتحسين محركات البحث (SEO) والتسويق الرقمي والتسويق عبر البريد الإلكتروني والرسائل النصية وتحسين معدل التحويل والخدمات الاستشارية ذات الصلة. خدماتنا مصممة للشركات العاملة في منطقة الخليج وعالمياً. يتم تحديد التسليمات والجداول الزمنية والرسوم المحددة في اتفاقيات الخدمة الفردية أو العروض المقدمة لكل عميل."
              )}
            </p>
          </div>

          {/* 3. User Responsibilities */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("3. User Responsibilities & Acceptable Use", "3. مسؤوليات المستخدم والاستخدام المقبول")}
            </h2>
            <p className="mb-3">
              {t("By using our website and services, you agree to:", "باستخدامك لموقعنا وخدماتنا، فإنك توافق على:")}
            </p>
            <ul className="list-disc ltr:pl-6 rtl:pr-6 space-y-2">
              <li>{t("Provide accurate, current, and complete information when contacting us, submitting forms, or engaging our services.", "تقديم معلومات دقيقة وحديثة وكاملة عند التواصل معنا أو تقديم النماذج أو الاستفادة من خدماتنا.")}</li>
              <li>{t("Use our website and services only for lawful purposes and in accordance with these Terms.", "استخدام موقعنا وخدماتنا فقط لأغراض قانونية ووفقاً لهذه الشروط.")}</li>
              <li>{t("Not attempt to gain unauthorized access to any portion of our website, systems, or networks.", "عدم محاولة الحصول على وصول غير مصرح به إلى أي جزء من موقعنا أو أنظمتنا أو شبكاتنا.")}</li>
              <li>{t("Not use our services to transmit harmful, offensive, or unlawful content.", "عدم استخدام خدماتنا لنقل محتوى ضار أو مسيء أو غير قانوني.")}</li>
              <li>{t("Not reproduce, distribute, or create derivative works from our content without prior written consent.", "عدم إعادة إنتاج أو توزيع أو إنشاء أعمال مشتقة من محتوانا دون موافقة كتابية مسبقة.")}</li>
              <li>{t("Comply with all applicable local, national, and international laws and regulations.", "الامتثال لجميع القوانين واللوائح المحلية والوطنية والدولية المعمول بها.")}</li>
            </ul>
          </div>

          {/* 4. Intellectual Property */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("4. Intellectual Property Rights", "4. حقوق الملكية الفكرية")}
            </h2>
            <p className="mb-3">
              {t(
                "All content on this website, including but not limited to text, graphics, logos, images, videos, software, code, designs, and the overall look and feel, is the property of Wavai Technology LLC or its licensors and is protected by copyright, trademark, and other intellectual property laws.",
                "جميع المحتوى على هذا الموقع, بما في ذلك على سبيل المثال لا الحصر النصوص والرسومات والشعارات والصور ومقاطع الفيديو والبرمجيات والأكواد والتصاميم والمظهر العام, هو ملك لشركة ويفاي تكنولوجي ذ.م.م أو المرخصين لها ومحمي بقوانين حقوق النشر والعلامات التجارية وغيرها من قوانين الملكية الفكرية."
              )}
            </p>
            <p>
              {t(
                "Work produced for clients under service agreements remains subject to the intellectual property terms outlined in the respective agreement. Unless otherwise specified in writing, Wavai retains ownership of proprietary tools, frameworks, and methodologies used in delivering services.",
                "تظل الأعمال المنتجة للعملاء بموجب اتفاقيات الخدمة خاضعة لشروط الملكية الفكرية المحددة في الاتفاقية المعنية. ما لم يُنص على خلاف ذلك كتابياً، تحتفظ ويفاي بملكية الأدوات والأطر والمنهجيات الخاصة المستخدمة في تقديم الخدمات."
              )}
            </p>
          </div>

          {/* 5. Limitation of Liability */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("5. Limitation of Liability", "5. حدود المسؤولية")}
            </h2>
            <p>
              {t(
                "To the fullest extent permitted by law, Wavai Technology LLC and its directors, employees, partners, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, business opportunities, or goodwill, arising out of or related to your use of our website or services. Our total liability for any claim arising under these Terms shall not exceed the total fees paid by you to Wavai in the twelve (12) months preceding the claim.",
                "إلى أقصى حد يسمح به القانون، لن تكون شركة ويفاي تكنولوجي ذ.م.م ومديروها وموظفوها وشركاؤها والشركات التابعة لها مسؤولين عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية أو عقابية, بما في ذلك على سبيل المثال لا الحصر خسارة الأرباح أو البيانات أو فرص العمل أو السمعة التجارية, الناشئة عن أو المتعلقة باستخدامك لموقعنا أو خدماتنا. لا يتجاوز إجمالي مسؤوليتنا عن أي مطالبة بموجب هذه الشروط إجمالي الرسوم المدفوعة منك لويفاي في الاثني عشر (12) شهراً السابقة للمطالبة."
              )}
            </p>
          </div>

          {/* 6. Disclaimer of Warranties */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("6. Disclaimer of Warranties", "6. إخلاء المسؤولية عن الضمانات")}
            </h2>
            <p>
              {t(
                "Our website and services are provided on an \"as is\" and \"as available\" basis without warranties of any kind, whether express, implied, or statutory. We do not warrant that our website will be uninterrupted, error-free, or free of harmful components. We disclaim all warranties, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. Any reliance on the content or services provided through our website is at your own risk.",
                "يتم تقديم موقعنا وخدماتنا على أساس \"كما هي\" و\"كما هي متاحة\" دون ضمانات من أي نوع، سواء كانت صريحة أو ضمنية أو قانونية. لا نضمن أن موقعنا سيكون بدون انقطاع أو خالياً من الأخطاء أو خالياً من المكونات الضارة. نخلي مسؤوليتنا عن جميع الضمانات، بما في ذلك الضمانات الضمنية للقابلية للتسويق والملاءمة لغرض معين وعدم الانتهاك. أي اعتماد على المحتوى أو الخدمات المقدمة عبر موقعنا يكون على مسؤوليتك الخاصة."
              )}
            </p>
          </div>

          {/* 7. Third-Party Services */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("7. Third-Party Services & Links", "7. خدمات وروابط الطرف الثالث")}
            </h2>
            <p>
              {t(
                "Our website may contain links to third-party websites, platforms, or services that are not owned or controlled by Wavai. We are not responsible for the content, privacy policies, or practices of any third-party websites or services. Our integration with platforms such as Shopify, Webflow, Klaviyo, and others does not imply endorsement or responsibility for those platforms. You acknowledge and agree that Wavai shall not be liable for any damage or loss caused by your use of or reliance on any third-party content, goods, or services.",
                "قد يحتوي موقعنا على روابط لمواقع أو منصات أو خدمات طرف ثالث لا تملكها أو تتحكم فيها ويفاي. نحن لسنا مسؤولين عن محتوى أو سياسات الخصوصية أو ممارسات أي مواقع أو خدمات طرف ثالث. لا يعني تكاملنا مع منصات مثل Shopify وWebflow وKlaviyo وغيرها المصادقة عليها أو تحمل المسؤولية عنها. أنت تُقر وتوافق على أن ويفاي لن تكون مسؤولة عن أي ضرر أو خسارة ناتجة عن استخدامك أو اعتمادك على أي محتوى أو سلع أو خدمات طرف ثالث."
              )}
            </p>
          </div>

          {/* 8. Termination */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("8. Termination", "8. الإنهاء")}
            </h2>
            <p>
              {t(
                "We reserve the right to suspend or terminate your access to our website and services at our sole discretion, without prior notice, for any reason, including but not limited to a breach of these Terms. Upon termination, your right to use our website and services will immediately cease. Provisions of these Terms that by their nature should survive termination, including intellectual property, limitation of liability, and governing law, shall remain in effect.",
                "نحتفظ بالحق في تعليق أو إنهاء وصولك إلى موقعنا وخدماتنا وفقاً لتقديرنا المطلق، دون إشعار مسبق، لأي سبب, بما في ذلك على سبيل المثال لا الحصر انتهاك هذه الشروط. عند الإنهاء، يتوقف حقك في استخدام موقعنا وخدماتنا فوراً. تظل أحكام هذه الشروط التي بطبيعتها يجب أن تبقى سارية بعد الإنهاء, بما في ذلك الملكية الفكرية وحدود المسؤولية والقانون الحاكم, نافذة المفعول."
              )}
            </p>
          </div>

          {/* 9. Changes to Terms */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("9. Changes to These Terms", "9. التغييرات على هذه الشروط")}
            </h2>
            <p>
              {t(
                "We reserve the right to modify or replace these Terms at any time. When we make material changes, we will update the \"Last updated\" date at the top of this page. Your continued use of our website and services after any changes constitutes your acceptance of the revised Terms. We encourage you to review these Terms periodically.",
                "نحتفظ بالحق في تعديل أو استبدال هذه الشروط في أي وقت. عند إجراء تغييرات جوهرية، سنقوم بتحديث تاريخ \"آخر تحديث\" في أعلى هذه الصفحة. استمرارك في استخدام موقعنا وخدماتنا بعد أي تغييرات يشكل قبولك للشروط المعدلة. نشجعك على مراجعة هذه الشروط بشكل دوري."
              )}
            </p>
          </div>

          {/* 10. Governing Law */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("10. Governing Law", "10. القانون الحاكم")}
            </h2>
            <p>
              {t(
                "These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates and the Emirate of Sharjah, without regard to conflict of law principles. Any disputes arising from or relating to these Terms or your use of our website and services shall be subject to the exclusive jurisdiction of the courts of Sharjah, UAE.",
                "تخضع هذه الشروط وتُفسر وفقاً لقوانين دولة الإمارات العربية المتحدة وإمارة الشارقة، بصرف النظر عن مبادئ تنازع القوانين. تخضع أي نزاعات ناشئة عن أو متعلقة بهذه الشروط أو استخدامك لموقعنا وخدماتنا للاختصاص القضائي الحصري لمحاكم الشارقة، الإمارات العربية المتحدة."
              )}
            </p>
          </div>

          {/* 11. Contact */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("11. Contact Information", "11. معلومات الاتصال")}
            </h2>
            <p className="mb-1">
              {t(
                "If you have any questions about these Terms of Service, please contact us:",
                "إذا كان لديك أي أسئلة حول شروط الخدمة هذه، يرجى التواصل معنا:"
              )}
            </p>
            <address className="not-italic space-y-1 mt-3">
              <p className="font-semibold text-foreground">Wavai Technology LLC</p>
              <p>
                {t(
                "ACE Hardware Building, 2nd Floor, Tunis Street, Hawally, Kuwait.",
                "مبنى ACE Hardware، الطابق الثاني، شارع تونس، حولي، الكويت."
                )}
              </p>
              <p>
                {t("Phone", "الهاتف")}:{" "}
                <a href="tel:+96597558522" dir="ltr" className="underline underline-offset-2 hover:text-foreground transition-colors">
                  +965 97558522
                </a>
              </p>
              <p>
                {t("Email", "البريد الإلكتروني")}:{" "}
                <a href="mailto:hello@wavai.com" className="underline underline-offset-2 hover:text-foreground transition-colors">
                  hello@wavai.com
                </a>
              </p>
            </address>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
